/**
 * Magic-link landing page (`/verify-domain`).
 *
 * Hit when an MSP child user clicks the login link in their OTP email.
 * Calls `/msp/domain-login/verify?token=...&domain_id=...` which validates
 * the token and sets the auth cookie on success. Then redirects to /portal.
 *
 * Backend `msp.py:959` constructs the link as
 *   `{settings.portal_url}/verify-domain?token=...&domain=...`
 * so this route MUST live at the host pointed to by `portal_url` — in prod
 * that should be `https://portal.ddmarc.com`.
 */
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "@ddmarc/ui";
import { useBranding } from "@/lib/branding-context";
import { CardV2 } from "@/components/primitives";
import { PortalBrand } from "@/components/portal-brand";

const searchSchema = z.object({
  token: z.string().default(""),
  // Backend accepts both `domain` (preferred) and `domain_id` (deprecated);
  // we forward whichever the email link carried.
  domain: z.string().optional(),
  domain_id: z.coerce.number().optional(),
});

export const Route = createFileRoute("/verify-domain")({
  validateSearch: searchSchema,
  component: VerifyDomainPage,
});

function VerifyDomainPage() {
  const { token, domain, domain_id } = Route.useSearch();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [domainName, setDomainName] = useState("");

  useEffect(() => {
    if (!token || (!domain && !domain_id)) {
      setStatus("error");
      setErrorMessage("Invalid verification link");
      return;
    }

    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout>;

    const verifyToken = async () => {
      try {
        const params = new URLSearchParams({ token });
        if (domain) params.set("domain", domain);
        else if (domain_id) params.set("domain_id", String(domain_id));

        const response = await fetch(
          `/api/v1/msp/domain-login/verify?${params.toString()}`,
          { signal: abortController.signal, credentials: "include" }
        );

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || "Verification failed");
        }

        const data = await response.json();
        if (abortController.signal.aborted) return;

        setDomainName(data.domain?.name || domain || "");
        setStatus("success");
        timeoutId = setTimeout(() => navigate({ to: "/portal" }), 1500);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        setStatus("error");
        setErrorMessage(
          err instanceof Error
            ? err.message
            : "This link is invalid or has expired"
        );
      }
    };

    verifyToken();

    return () => {
      abortController.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [token, domain, domain_id, navigate]);

  return (
    <div
      data-ui="v2"
      className="flex min-h-screen flex-col items-center justify-center bg-background p-4"
    >
      <div className="mb-6 flex flex-col items-center gap-3">
        <PortalBrand branding={branding} size="lg" />
      </div>

      <CardV2
        className="w-full max-w-md"
        title={
          <span className="text-center text-[16px] font-semibold">
            {status === "loading" && "Verifying..."}
            {status === "success" && "Welcome!"}
            {status === "error" && "Verification failed"}
          </span>
        }
        subtitle={
          <span className="block text-center">
            {status === "loading" &&
              "Please wait while we verify your access..."}
            {status === "success" &&
              (domainName
                ? `Accessing ${domainName} dashboard...`
                : "Redirecting to your dashboard...")}
            {status === "error" && errorMessage}
          </span>
        }
      >
        <div className="flex justify-center pb-1">
          {status === "loading" && (
            <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
          )}
          {status === "success" && (
            <CheckCircle className="h-8 w-8 text-[var(--v2-good)]" />
          )}
          {status === "error" && (
            <XCircle className="h-8 w-8 text-[var(--v2-danger)]" />
          )}
        </div>

        {status === "error" && (
          <Button asChild className="mt-3 w-full">
            <Link to="/login">Back to login</Link>
          </Button>
        )}
      </CardV2>
    </div>
  );
}
